import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { Copy, Download, Share2, Check } from 'lucide-react';
import Footer from '../components/Footer';
import CircularProgress from '../components/CircularProgress';
import Wordmark from '../components/Wordmark';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Results = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState({});
  const trustId = location.state?.trust_id;

  useEffect(() => {
    fetchResults();
  }, [id]);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/results/${id}`);
      setResults(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load results');
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied({ ...copied, [key]: true });
      setTimeout(() => {
        setCopied({ ...copied, [key]: false });
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareTrustPage = async () => {
    const trustUrl = `${window.location.origin}/trust/${results.trust_id}`;
    await copyToClipboard(trustUrl, 'trust');
  };

  const toggleTaskCompletion = async (taskId) => {
    try {
      const response = await axios.post(`${API}/results/${id}/tasks/${taskId}/complete`);
      if (response.data.success) {
        // Update local state
        setResults({
          ...results,
          monthly_tasks: results.monthly_tasks.map(task =>
            task.id === taskId ? { ...task, completed: response.data.completed } : task
          )
        });
      }
    } catch (err) {
      console.error('Failed to toggle task:', err);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-[#1a1a1a]/60">Loading results...</p>
      </div>
    );
  }

  if (error || !results) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#1a1a1a] mb-4">{error || 'Results not found'}</p>
          <Button onClick={() => navigate('/')} className="bg-[#1a1a1a] text-white">
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="border-b border-[#e0ddd8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-5 flex items-center justify-between gap-3">
          <Wordmark />
          
          <Button
            onClick={shareTrustPage}
            className="bg-[#0a5c44] text-white hover:bg-[#0a5c44]/90 transition-colors flex items-center gap-2 h-11 px-3 sm:px-4"
          >
            {copied.trust ? (
              <>
                <Check className="w-4 h-4" />
                <span className="hidden sm:inline">Copied!</span>
              </>
            ) : (
              <>
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share Trust Page</span>
                <span className="sm:hidden">Share</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 w-full">
        {/* Compliance Score */}
        <div className="mb-10 sm:mb-12 bg-[#f5f3ef] border border-[#e0ddd8] rounded-2xl p-6 sm:p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div className="flex-1">
            <h2 className="text-2xl sm:text-3xl font-normal mb-2 leading-tight" style={{ fontFamily: 'Instrument Serif, serif' }}>
              Your Compliance Score
            </h2>
            <p className="text-sm sm:text-base text-[#1a1a1a]/60">
              Complete your monthly checklist to improve your score.
            </p>
          </div>
          <div className="flex justify-center sm:block">
            <CircularProgress value={results.compliance_score || 0} />
          </div>
        </div>

        <div className="mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-normal mb-2 leading-tight" style={{ fontFamily: 'Instrument Serif, serif' }}>
            Your Compliance Package
          </h1>
          <p className="text-sm sm:text-base text-[#1a1a1a]/60 mb-4">
            {results.answers?.q1 || 'Your company'} · Everything you need to demonstrate your security posture to prospects.
          </p>
        </div>

        <Tabs defaultValue="roadmap" className="w-full">
          {/* Mobile: scrollable horizontal tabs. Desktop: 5-column grid. */}
          <TabsList className="w-full mb-6 sm:mb-8 flex sm:grid sm:grid-cols-5 overflow-x-auto sm:overflow-visible scrollbar-thin gap-1 h-auto sm:h-10 p-1">
            <TabsTrigger value="roadmap" className="shrink-0 sm:shrink whitespace-nowrap">Roadmap</TabsTrigger>
            <TabsTrigger value="data_handling" className="shrink-0 sm:shrink whitespace-nowrap">Data Handling</TabsTrigger>
            <TabsTrigger value="access_control" className="shrink-0 sm:shrink whitespace-nowrap">Access Control</TabsTrigger>
            <TabsTrigger value="incident_response" className="shrink-0 sm:shrink whitespace-nowrap">Incident Response</TabsTrigger>
            <TabsTrigger value="monthly_tasks" className="shrink-0 sm:shrink whitespace-nowrap">Monthly Tasks</TabsTrigger>
          </TabsList>

          {/* Roadmap Tab */}
          <TabsContent value="roadmap">
            <div className="space-y-4">
              {results.roadmap.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white border border-[#e0ddd8] rounded-xl p-6"
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-xl font-semibold" style={{ fontFamily: 'Instrument Serif, serif' }}>
                      {item.title}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getPriorityColor(item.priority)}`}>
                      {item.priority}
                    </span>
                  </div>
                  <p className="text-[#1a1a1a]/70 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* Data Handling Policy Tab */}
          <TabsContent value="data_handling">
            <div className="bg-white border border-[#e0ddd8] rounded-xl p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-semibold" style={{ fontFamily: 'Instrument Serif, serif' }}>
                  {results.data_handling_policy.title}
                </h2>
                <div className="flex gap-2">
                  <Button
                    onClick={() => copyToClipboard(results.data_handling_policy.content, 'data_handling')}
                    variant="outline"
                    className="border-[#e0ddd8]"
                  >
                    {copied.data_handling ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    <span className="ml-2">Copy</span>
                  </Button>
                </div>
              </div>
              <div className="prose prose-lg max-w-none">
                <div className="whitespace-pre-wrap text-[#1a1a1a]/80 leading-relaxed">
                  {results.data_handling_policy.content}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Access Control Policy Tab */}
          <TabsContent value="access_control">
            <div className="bg-white border border-[#e0ddd8] rounded-xl p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-semibold" style={{ fontFamily: 'Instrument Serif, serif' }}>
                  {results.access_control_policy.title}
                </h2>
                <div className="flex gap-2">
                  <Button
                    onClick={() => copyToClipboard(results.access_control_policy.content, 'access_control')}
                    variant="outline"
                    className="border-[#e0ddd8]"
                  >
                    {copied.access_control ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    <span className="ml-2">Copy</span>
                  </Button>
                </div>
              </div>
              <div className="prose prose-lg max-w-none">
                <div className="whitespace-pre-wrap text-[#1a1a1a]/80 leading-relaxed">
                  {results.access_control_policy.content}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Incident Response Policy Tab */}
          <TabsContent value="incident_response">
            <div className="bg-white border border-[#e0ddd8] rounded-xl p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-semibold" style={{ fontFamily: 'Instrument Serif, serif' }}>
                  {results.incident_response_policy.title}
                </h2>
                <div className="flex gap-2">
                  <Button
                    onClick={() => copyToClipboard(results.incident_response_policy.content, 'incident_response')}
                    variant="outline"
                    className="border-[#e0ddd8]"
                  >
                    {copied.incident_response ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    <span className="ml-2">Copy</span>
                  </Button>
                </div>
              </div>
              <div className="prose prose-lg max-w-none">
                <div className="whitespace-pre-wrap text-[#1a1a1a]/80 leading-relaxed">
                  {results.incident_response_policy.content}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Monthly Tasks Tab */}
          <TabsContent value="monthly_tasks">
            <div className="bg-white border border-[#e0ddd8] rounded-xl p-8">
              <h2 className="text-3xl font-semibold mb-6" style={{ fontFamily: 'Instrument Serif, serif' }}>
                This Month's Tasks
              </h2>
              
              <div className="space-y-4 mb-8">
                {results.monthly_tasks && results.monthly_tasks.length > 0 ? (
                  results.monthly_tasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-start gap-4 p-4 border border-[#e0ddd8] rounded-lg hover:bg-[#f5f3ef]/30 transition-colors"
                    >
                      <Checkbox
                        id={`task-${task.id}`}
                        checked={task.completed}
                        onCheckedChange={() => toggleTaskCompletion(task.id)}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <label
                          htmlFor={`task-${task.id}`}
                          className={`block font-medium cursor-pointer ${
                            task.completed ? 'line-through text-[#1a1a1a]/50' : 'text-[#1a1a1a]'
                          }`}
                        >
                          {task.title}
                        </label>
                        <p className={`text-sm mt-1 ${
                          task.completed ? 'text-[#1a1a1a]/40' : 'text-[#1a1a1a]/70'
                        }`}>
                          {task.description}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-[#1a1a1a]/60">No tasks available yet.</p>
                )}
              </div>

              <div className="border-t border-[#e0ddd8] pt-6">
                <p className="text-sm text-[#1a1a1a]/60">
                  New tasks added every month. Your Trust Page stays live as long as your subscription is active.
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      <Footer />
    </div>
  );
};

export default Results;
